import { OnboardingLayout } from "@wayflyer/flyui";
import { useIntl } from "react-intl";
import { useSpaNavigate } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";
import { useQueryClient } from "@tanstack/react-query";
import { addWarning } from "@wayflyer/blackbox-fe";

import { getFundingApplicationStagesQueryOptions } from "../../queries/funding-application-configuration/queries";
import { composePath, PAGE_TYPE } from "../../hooks/navigation-next/use-funding-application-navigation-next";
import {
  isRejectedFundingApplicationStateTransition,
  useFundingApplication,
  useFundingApplicationMutation,
  useFundingApplicationStateMutation,
} from "../../queries/funding-application/queries";
import { calculateEligibleAmount } from "../../utils/eligible-funding-amount";

import { MESSAGES } from "./message-definitions";
import { SelectFundingForm } from "./select-funding-form";

const FORM_ID = "select-funding-form";

export const SelectFundingPage = () => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const queryClient = useQueryClient();
  const { data } = useFundingApplication(customerDomain);
  const { isPending: isSubmitting } = useFundingApplicationMutation(customerDomain);
  const { eligibleAmount, eligibleCurrency } = data
    ? calculateEligibleAmount(
        data.reportedRevenue,
        data.reportedRevenueCurrency,
        data.precalculatedIndicativeOfferAmount,
        data.precalculatedIndicativeOfferCurrency
      )
    : { eligibleAmount: 0, eligibleCurrency: "" };

  const setApplicationState = useFundingApplicationStateMutation(customerDomain);
  const { navigate } = useSpaNavigate("FundingApp");

  const setStateAndContinue = async () => {
    try {
      await setApplicationState.mutateAsync({
        applicationState: "in_progress",
      });
    } catch (error) {
      if (isRejectedFundingApplicationStateTransition(error)) {
        // Hook's onError has already redirected to root — stop here.
        return;
      }
      throw error;
    }

    try {
      const stages = await queryClient.fetchQuery({
        ...getFundingApplicationStagesQueryOptions(customerDomain),
        retry: 1,
      });
      // Determine the next page based on whether platform selection is included
      // If stages is null (legacy flow) or includePlatformSelection is true, default to select platforms page
      // Otherwise, skip to banking page
      const nextPage =
        stages.data === null || stages.data.includePlatformSelection ? PAGE_TYPE.SELECT_PLATFORMS : PAGE_TYPE.BANKING;
      navigate(composePath(nextPage));
    } catch (error) {
      addWarning("Failed to fetch funding application stages configuration", {
        customerDomain,
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  };

  return (
    <OnboardingLayout
      title={
        data.isWebsitePrefilled
          ? intl.formatMessage(MESSAGES.fundingPotentialAltTitle)
          : `${intl.formatMessage(MESSAGES.fundingPotentialAmountTitle)} ${intl.formatNumber(eligibleAmount, {
              style: "currency",
              // HACK: See https://github.com/wayflyer/wayflyer/pull/21846
              currency: eligibleCurrency as string | undefined,
              currencyDisplay: eligibleCurrency === "USD" ? "narrowSymbol" : "symbol",
              maximumFractionDigits: 0,
            })}*`
      }
      actions={{
        primary: {
          content: intl.formatMessage(MESSAGES.continueToConnectionsLabel),
          onAction: () => {
            const form = document.getElementById(FORM_ID) as HTMLFormElement;
            if (form) {
              form.requestSubmit();
            }
          },
          analyticsId: "select-funding-submit",
          loading: isSubmitting,
          disabled: isSubmitting,
        },
      }}
    >
      <SelectFundingForm onContinue={setStateAndContinue} formId={FORM_ID} />
    </OnboardingLayout>
  );
};
